import React from 'react';
import { t } from 'i18next';

import type { SelectOption } from '@app/Utilities/SelectMap';
import type { IndUpdateDTO } from '@api/DTO/Ind/Ind/dto';

import { StatusSourceForm } from './StatusSourceForm';
import type { StatusSourceInitialValues } from './types';

import { useRepository } from '@app/Hooks/useRepository';
import { IndRepository, UserSourcesRepository } from '@api/index';
import type { Ind } from '@api/Entities/Ind/Ind';
import { getIndStatusSelectOptions } from '@api/Entities/Ind/Ind';

import { EditableFormCard } from '@app/Components/Forms/EditableFormCard';
import { useModalStack } from '@app/UI/Modals/ModalStackProvider';

/**
 * Props for {@link StatusSourceFormDelegator}.
 *
 * Architectural intent:
 * - This component owns **workflows** (API calls, modals, option refresh).
 * - It bridges domain concerns and UI-only forms.
 * - It decides *how* user intent is fulfilled.
 */
export type StatusSourceFormDelegatorProps = {
  /**
   * ID of the impacted user (the user whose sources are being managed).
   *
   * This is intentionally explicit to support UAC / impersonation scenarios.
   */
  userId: number;

  /**
   * ID of the profile being updated.
   * Used when submitting status/source updates.
   */
  profileId: number;

  /** Initial values to seed the form */
  initialValues?: Partial<StatusSourceInitialValues>;

  /**
   * Optional callback fired after the IND has been successfully updated.
   *
   * @param ind Updated IND entity
   */
  onSave?: (ind: Ind) => Promise<void>;
};

/**
 * StatusSourceFormDelegator
 *
 * Responsibilities:
 * - Fetch and manage user source options
 * - Handle "add user source" workflow (modal + result)
 * - Submit form payloads to repositories
 * - Manage loading / saving state
 *
 * This component deliberately:
 * - Does NOT render individual form fields
 * - Does NOT own form state
 * - Does NOT mutate form values directly
 */
export const StatusSourceFormDelegator = ({
  userId,
  profileId,
  initialValues,
  onSave,
}: StatusSourceFormDelegatorProps) => {
  const userSourceRepository = useRepository(UserSourcesRepository);
  const indRepository = useRepository(IndRepository);

  /** Indicates whether the form submission is in progress */
  const [isSaving, setIsSaving] = React.useState<boolean>(false);

  /**
   * One-shot request to update the `indSource` field inside the form.
   *
   * This is passed downward and consumed by the form via an effect.
   */
  const [updatedIndSource, setUpdatedIndSource] = React.useState<number | null>(null);

  /** Available source options for the select */
  const [userSources, setUserSources] = React.useState<Array<SelectOption<number>>>([]);

  /** Indicates whether source options are currently loading */
  const [userSourcesLoading, setUserSourcesLoading] = React.useState<boolean>(true);

  /** Modal orchestration API */
  const { open } = useModalStack();

  const statusOptions = React.useMemo(() => getIndStatusSelectOptions(t), [t]);

  /**
   * Fetch the list of user sources for the impacted user.
   *
   * This is called:
   * - on initial mount
   * - optionally after workflows that affect sources
   */
  const refreshUserSources = React.useCallback(async () => {
    setUserSourcesLoading(true);
    try {
      const col = await userSourceRepository.getUserSources(userId, {
        limit: 100,
      });

      setUserSources(
        col.getItems().map((s) => ({
          value: s.getSourceId(),
          label: s.getSourceName(),
        }))
      );
    } finally {
      setUserSourcesLoading(false);
    }
  }, [userSourceRepository, userId]);

  /**
   * Load source options on initial mount.
   */
  React.useEffect(() => {
    void refreshUserSources();
  }, [refreshUserSources]);

  /**
   * Handle form submission.
   *
   * - Receives mapped DTO from the form
   * - Persists it via repository
   * - Emits `onSave` callback if provided
   */
  const handleSubmit = React.useCallback(
    async (values: IndUpdateDTO) => {
      setIsSaving(true);
      try {
        const updated = await indRepository.updateInd(profileId, values);
        await onSave?.(updated);
      } finally {
        setIsSaving(false);
      }
    },
    [indRepository, profileId, onSave]
  );

  /**
   * Handle the "add user source" intent.
   *
   * Workflow:
   * 1. Open create-source modal
   * 2. Await created entity
   * 3. Append new option locally (fast UX)
   * 4. Request the form to select the new source
   *
   * Cancellation is silently ignored.
   */
  const handleAddUserSource = React.useCallback(async () => {
    try {
      const createdUserSource = await open('userSourceCreate', { userId });

      setUserSources((prev) => [
        ...prev,
        {
          value: createdUserSource.getSourceId(),
          label: createdUserSource.getSourceName(),
        },
      ]);

      // One-shot request for the form to update its field value
      setUpdatedIndSource(createdUserSource.getSourceId());
    } catch {
      // Modal cancelled — no action required
      return;
    }
  }, [open, userId]);

  return (
    <EditableFormCard title={t('agent.profile.statusSourceForm.title')}>
      {({ editing }) => (
        <StatusSourceForm
          initialValues={initialValues}
          statusOptions={statusOptions}
          updatedIndSource={updatedIndSource}
          sourceOptions={userSources}
          loadingSourceOptions={userSourcesLoading}
          onSubmit={handleSubmit}
          onAddUserSource={handleAddUserSource}
          mode={editing ? 'edit' : undefined}
          isSaving={isSaving}
        />
      )}
    </EditableFormCard>
  );
};
